import { LogoMark } from "@/components/Logo";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, QrCode, MessageSquare, Users, Send, Settings,
  LogOut, History, Wallet, ListTree, Shield, CalendarRange, Megaphone, ScrollText, HeartPulse,
  Receipt, Smartphone, UserCog, Gauge,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { api } from "@/lib/apiClient";
import { setTenantTz } from "@/lib/tz";

const NAV = [
  { to: "/", label: "Painel", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/whatsapp", label: "Conexão WhatsApp", icon: QrCode, id: "nav-whatsapp" },
  { to: "/menu", label: "Menu do Bot", icon: ListTree, id: "nav-menu" },
  { to: "/templates", label: "Templates", icon: MessageSquare, id: "nav-templates" },
  { to: "/recebimentos", label: "Recebimentos PIX", icon: Wallet, id: "nav-payments" },
  { to: "/cobranca-avulsa", label: "Cobrança Avulsa", icon: Receipt, id: "nav-manual-charges" },
  { to: "/clientes", label: "Clientes", icon: Users, id: "nav-clients" },
  { to: "/score", label: "Score de Clientes", icon: Gauge, id: "nav-score" },
  { to: "/cobranca-lote", label: "Cobrança em Lote", icon: CalendarRange, id: "nav-bulk-billing" },
  { to: "/envios", label: "Envios", icon: Send, id: "nav-messages" },
  { to: "/logs", label: "Logs", icon: ScrollText, id: "nav-logs" },
  { to: "/saude", label: "Saúde do Sistema", icon: HeartPulse, id: "nav-health" },
  { to: "/atendimentos", label: "Atendimentos", icon: History, id: "nav-chat" },
  { to: "/config", label: "Configurações", icon: Settings, id: "nav-settings" },
];
const TEAM_NAV = { to: "/equipe", label: "Equipe", icon: UserCog, id: "nav-team" };
// Permissao exigida por tela (as demais sao so do administrador). O servidor tambem bloqueia.
export const NAV_PERM = {
  "/": "dashboard.view", "/recebimentos": "payments.view", "/cobranca-avulsa": "charges.manage",
  "/clientes": "clients.view", "/score": "clients.view", "/cobranca-lote": "billing.bulk", "/envios": "messages.view",
  "/logs": "logs.view", "/atendimentos": "chat.reply", "/marketing": "marketing.manage",
};
export const isFullRole = (u) => ["admin", "superadmin"].includes(u?.team_role || "admin");
export const canSee = (u, path) => isFullRole(u) || (NAV_PERM[path] && (u?.perms || []).includes(NAV_PERM[path]));
const MARKETING_NAV = { to: "/marketing", label: "Marketing", icon: Megaphone, id: "nav-marketing" };
const ADMIN_NAV = { to: "/admin", label: "Superadmin", icon: Shield, id: "nav-admin" };
const APP_NAV = { to: "/app", label: "App Android", icon: Smartphone, id: "nav-app" };

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  useEffect(() => {
    let off = false;
    api.get("/tenant/settings")
      .then(({ data }) => { if (!off && data?.timezone) setTenantTz(data.timezone); })
      .catch(() => {});
    return () => { off = true; };
  }, [user?.id]);
  const navigate = useNavigate();
  const full = isFullRole(user);
  const navItems = full
    ? [...NAV, TEAM_NAV, ...(user?.marketing_module_enabled ? [MARKETING_NAV] : []), ...(user?.app_module_enabled || user?.is_superadmin ? [APP_NAV] : []), ...(user?.is_superadmin ? [ADMIN_NAV] : [])]
    : [...NAV, ...(user?.marketing_module_enabled ? [MARKETING_NAV] : [])].filter((n) => canSee(user, n.to));

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-300 grain-overlay">
      <aside className="w-64 shrink-0 border-r border-slate-800 bg-slate-900/60 flex flex-col">
        <div className="px-5 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <LogoMark className="w-9 h-9 shrink-0" />
            <div>
              <div className="font-outfit text-white text-sm font-bold tracking-tight">Quita<span className="text-emerald-500">í</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Painel Multi-Tenant</div>
            </div>
          </div>
          <div
            data-testid="tenant-switcher"
            className="mt-5 px-3 py-2 rounded-md border border-slate-800 bg-slate-900"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Empresa Ativa</div>
            <div className="text-sm text-white font-medium truncate" data-testid="tenant-name">
              {user?.tenant_name || "—"}
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.id}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                    isActive
                      ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                      : "text-slate-400 hover:text-white hover:bg-slate-800/70 border border-transparent"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-emerald-400 text-xs font-bold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="p-2 rounded-md text-slate-500 hover:text-emerald-400 hover:bg-slate-800 transition-colors duration-200"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-[1400px] mx-auto p-8">{children}</div>
      </main>
    </div>
  );
}
